import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import {
  User, CreditCard, Calendar, Car, CheckCircle2, 
  XCircle, Clock, DollarSign, Loader2, Crown
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../../lib/utils';

export function MemberDetailsModal({ tripId, memberId, memberName, isOpen, onClose }) {
  const { api } = useAuth();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && memberId) {
      fetchDetails();
    }
  }, [isOpen, memberId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/members/${memberId}/details`);
      setDetails(response.data);
    } catch (error) {
      console.error('Error fetching member details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentProgress = () => {
    if (!details || details.total_payments === 0) return 0;
    return (details.payments_made / details.total_payments) * 100;
  };

  const getBalanceColor = () => {
    if (!details) return 'text-slate-500';
    if (details.balance > 0) return 'text-green-500';
    if (details.balance < 0) return 'text-red-500';
    return 'text-slate-500';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-xl">
              {details?.avatar || '👤'}
            </div>
            <div>
              <span className="block">{details?.username || memberName}</span>
              <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
                {details?.email}
              </span>
            </div>
            {details?.role === 'organizer' && (
              <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 ml-auto">
                <Crown className="w-3 h-3 mr-1" />
                Organizador
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : details ? (
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Payment Summary */}
              <Card className="border-slate-200 dark:border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="w-5 h-5 text-blue-500" />
                    <h3 className="font-semibold">Resumo Financeiro</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Progress */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-500 dark:text-slate-400">
                          Pagamentos realizados
                        </span>
                        <span className="font-medium">
                          {details.payments_made}/{details.total_payments}
                        </span>
                      </div>
                      <Progress value={getPaymentProgress()} className="h-2" />
                    </div>
                    
                    {/* Amounts */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Total Devido</p>
                        <p className="font-semibold text-slate-900 dark:text-white">
                          R$ {details.total_due?.toFixed(2)}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Total Pago</p>
                        <p className="font-semibold text-green-600">
                          R$ {details.total_paid?.toFixed(2)}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Saldo</p>
                        <p className={cn("font-semibold", getBalanceColor())}>
                          R$ {Math.abs(details.balance || 0).toFixed(2)}
                          {details.balance < 0 && ' (deve)'}
                          {details.balance > 0 && ' (crédito)'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Payment Details */}
                    {details.payment_details?.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Detalhes dos Pagamentos
                        </p>
                        {details.payment_details.map((payment) => (
                          <div 
                            key={payment.payment_id}
                            className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                          >
                            <div className="flex items-center gap-2">
                              {payment.is_paid ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              ) : (
                                <Clock className="w-4 h-4 text-yellow-500" />
                              )}
                              <span className="text-slate-700 dark:text-slate-300">
                                {payment.payment_title}
                              </span>
                            </div>
                            <div className="text-right">
                              <span className={cn(
                                "font-medium",
                                payment.is_paid ? "text-green-600" : "text-slate-500"
                              )}>
                                R$ {payment.amount_paid?.toFixed(2)}
                              </span>
                              <span className="text-slate-400 dark:text-slate-500 dark:text-slate-400">
                                {' / R$ '}{payment.amount_due?.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Itineraries */}
              {details.assigned_itineraries?.length > 0 && (
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="w-5 h-5 text-purple-500" />
                      <h3 className="font-semibold">Atividades ({details.assigned_itineraries.length})</h3>
                    </div>
                    
                    <div className="space-y-2">
                      {details.assigned_itineraries.slice(0, 5).map((item) => (
                        <div 
                          key={item.item_id}
                          className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                        >
                          <span className="text-slate-700 dark:text-slate-300">
                            {item.title}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {format(parseISO(item.date), "dd/MM", { locale: ptBR })}
                          </span>
                        </div>
                      ))}
                      {details.assigned_itineraries.length > 5 && (
                        <p className="text-xs text-slate-500 text-center pt-2">
                          +{details.assigned_itineraries.length - 5} atividades
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Carpools */}
              {details.carpool_info?.length > 0 && (
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Car className="w-5 h-5 text-cyan-500" />
                      <h3 className="font-semibold">Caronas ({details.carpool_info.length})</h3>
                    </div>
                    
                    <div className="space-y-2">
                      {details.carpool_info.map((carpool) => (
                        <div 
                          key={carpool.carpool_id}
                          className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                        >
                          <span className="text-slate-700 dark:text-slate-300">
                            {carpool.route}
                          </span>
                          <Badge variant={carpool.is_driver ? "default" : "outline"} className="text-xs">
                            {carpool.is_driver ? '🚗 Motorista' : '👤 Passageiro'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Join Date */}
              <div className="text-center text-sm text-slate-500 dark:text-slate-400">
                Membro desde {format(parseISO(details.joined_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </div>
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            Não foi possível carregar os detalhes do membro
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
